import * as React from 'react';
import { memo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { LAYERS } from 'components/constants';
import { Search as SearchIcon, Keyboard as KeyboardIcon } from 'lucide-react';

import { Colors } from 'services/ColorService';
import { PaletteMode } from 'core/state';

import { IPaletteRowItem, IPaletteSection } from './paletteTypes';
import { PaletteResultsList } from './PaletteResultsList';
import { PaletteEntityActions } from './PaletteEntityActions';
import { ShortcutsCheatSheet, ICheatSheetSection } from './ShortcutsCheatSheet';

export interface ICommandPaletteProps {
  query: string;
  placeholder: string;
  onQueryChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  mode: PaletteMode;
  sections: IPaletteSection[];
  actionItems: IPaletteRowItem[];
  actionsTitle: string;
  backHint: string;
  selectedIndex: number;
  onHover: (index: number) => void;
  loading: boolean;
  emptyText: string;
  footer: React.ReactNode;
  hasContent: boolean;
  onOverlayMouseDown: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  /** Cheat-sheet title + sections, shown when `mode === 'help'`. */
  helpTitle: string;
  helpSections: ICheatSheetSection[];
}

/**
 * Presentational palette shell — a `createPortal` overlay (z-index 1100, above
 * the Modal atom's 1000) with a centered ~640px card, rendered like
 * `GlobalModal`. Not an antd Modal. Closes on a backdrop `mousedown`; clicks on
 * the card are swallowed so they never bubble to the backdrop.
 */
export const CommandPalette = memo<ICommandPaletteProps>((props) => {
  const {
    query,
    placeholder,
    onQueryChange,
    onKeyDown,
    mode,
    sections,
    actionItems,
    actionsTitle,
    backHint,
    selectedIndex,
    onHover,
    loading,
    emptyText,
    footer,
    hasContent,
    onOverlayMouseDown,
    inputRef,
    helpTitle,
    helpSections,
  } = props;

  const listRef = useRef<HTMLDivElement>(null);

  const isHelp = mode === 'help';

  // Keep the active row visible as the flat cursor moves.
  useEffect(() => {
    const active = listRef.current?.querySelector('[data-active="true"]');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, mode]);

  return createPortal(
    <Overlay
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onOverlayMouseDown();
      }}
    >
      <Card onMouseDown={(event) => event.stopPropagation()}>
        {isHelp ? (
          <HelpHeader>
            <KeyboardIcon size={18} strokeWidth={2} color={Colors.Gray} />
            <HelpTitle>{helpTitle}</HelpTitle>
          </HelpHeader>
        ) : (
          <InputRow>
            <SearchIcon size={18} strokeWidth={2} color={Colors.Gray} />
            <Input
              ref={inputRef}
              autoFocus
              type="text"
              value={query}
              placeholder={placeholder}
              onChange={(event) => onQueryChange(event.target.value)}
              onKeyDown={onKeyDown}
            />
            {loading ? <LoadingDot /> : null}
          </InputRow>
        )}

        <Body ref={listRef}>
          {isHelp ? (
            <ShortcutsCheatSheet sections={helpSections} />
          ) : mode === 'entityActions' ? (
            <PaletteEntityActions
              title={actionsTitle}
              backHint={backHint}
              items={actionItems}
              selectedIndex={selectedIndex}
              onHover={onHover}
            />
          ) : hasContent ? (
            <PaletteResultsList
              sections={sections}
              selectedIndex={selectedIndex}
              onHover={onHover}
            />
          ) : (
            <Empty>{emptyText}</Empty>
          )}
        </Body>

        <Footer>{footer}</Footer>
      </Card>
    </Overlay>,
    document.body,
  );
});

const Overlay = styled.div`
  position: fixed;
  z-index: ${LAYERS.COMMAND_PALETTE};
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 12vh;
  background: ${Colors.ModalBackground};
  overflow: hidden;
`;

const Card = styled.div`
  width: 640px;
  max-width: calc(100vw - 32px);
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  background: ${Colors.White};
  border: 1px solid ${Colors.LightGray};
  border-radius: 12px;
  box-shadow: 0 20px 60px ${Colors.BlueShadowRow};
  overflow: hidden;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid ${Colors.LightGray};
`;

const HelpHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid ${Colors.LightGray};
`;

const HelpTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${Colors.Black};
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font: inherit;
  font-size: 16px;
  color: ${Colors.Black};
  background: transparent;

  &::placeholder {
    color: ${Colors.DeactivatedGrey};
  }
`;

const LoadingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${Colors.Blue};
  animation: palettePulse 0.9s ease-in-out infinite;

  @keyframes palettePulse {
    0%,
    100% {
      opacity: 0.3;
    }
    50% {
      opacity: 1;
    }
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const Empty = styled.div`
  padding: 28px 12px;
  text-align: center;
  font-size: 13px;
  color: ${Colors.Gray};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  border-top: 1px solid ${Colors.LightGray};
  font-size: 11px;
  color: ${Colors.Gray};
`;
